/**
 * Fixed bottom audio player, sitting directly on top of the bottom nav (nav height + safe-area inset).
 * It also keeps the "Nastavi slušanje" position on the device (Q23): recorded while playing, on pause
 * and on close, forgotten when the session ends; `resumeSession` starts a session at that position.
 */
import { useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Pause, SkipBack, SkipForward, X, Volume2, VolumeX } from 'lucide-react';
import { SessionImageFallback } from '@/components/ui';
import { useAudioPlayerStore, useLanguage, useListeningResumeStore } from '@/store';
import { getAudioPlayerTranslation } from '@/utils';
import { ROUTES } from '@/config/routes';
import { sessionsApi } from '@/services/api/sessions';
import type { Session, LocalizedSession, SessionDisplay } from '@/types';

type PlayableSession = Session | LocalizedSession | SessionDisplay;

/** A fresh audio URL, no access (401/403 or a null URL), or another failure. */
type PlayableUrlResult =
  | { kind: 'url'; url: string }
  | { kind: 'denied' }
  | { kind: 'failed' };

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function FixedAudioPlayer() {
  const navigate = useNavigate();
  const language = useLanguage();
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  const t = (key: Parameters<typeof getAudioPlayerTranslation>[0]) => getAudioPlayerTranslation(key, language);

  const {
    currentSession,
    isPlaying,
    isVisible,
    currentTime,
    duration,
    volume,
    isMuted,
    startAt,
    setIsPlaying,
    setCurrentTime,
    setDuration,
    setVolume,
    setIsMuted,
    setAudioUrl,
    closePlayer,
    clearStartAt,
  } = useAudioPlayerStore();
  const recordListening = useListeningResumeStore((state) => state.record);
  const forgetListening = useListeningResumeStore((state) => state.clearSession);
  // A position waiting for the new source's metadata (seeking before it is unreliable on iOS): a
  // resume (Q23), or where playback was when an expired signed URL was replaced (SEC2).
  const pendingSeekRef = useRef<number | null>(null);

  const recordPosition = useCallback((force: boolean) => {
    const audio = audioRef.current;
    // While a resume seek waits for metadata the element reports 0: keep the stored position.
    if (audio && currentSession && pendingSeekRef.current === null) {
      recordListening(currentSession.id, audio.currentTime, audio.duration, { force });
    }
  }, [currentSession, recordListening]);

  // Premium audio URLs are signed and expire (SEC2). A just-fetched URL that
  // has not loaded yet: an error on that one is final, so a broken file
  // cannot loop. The position to restore after a refresh is pendingSeekRef.
  const refreshedUrlRef = useRef<string | null>(null);

  const fetchPlayableUrl = useCallback(async (sessionId: string): Promise<PlayableUrlResult> => {
    try {
      const fresh = await sessionsApi.getSession(sessionId, language);
      return fresh.audio_url ? { kind: 'url', url: fresh.audio_url } : { kind: 'denied' };
    } catch (error) {
      const status = (error as { status?: unknown } | null)?.status;
      return status === 401 || status === 403 ? { kind: 'denied' } : { kind: 'failed' };
    }
  }, [language]);

  // A session without a URL (e.g. a list fetched before sign-in): fetch one.
  useEffect(() => {
    if (!currentSession || currentSession.audio_url) return;
    const sessionId = currentSession.id;
    let cancelled = false;
    void fetchPlayableUrl(sessionId).then((result) => {
      if (cancelled) return;
      if (result.kind === 'url') {
        refreshedUrlRef.current = result.url;
        setAudioUrl(sessionId, result.url);
      } else {
        closePlayer();
      }
    });
    return () => {
      cancelled = true;
    };
  }, [currentSession, fetchPlayableUrl, setAudioUrl, closePlayer]);

  // Media error (an expired URL after a long pause): fetch a fresh URL once
  // and resume where playback was. If access lapsed meanwhile, close the
  // player (as when the first fetch finds no access); on any other failure,
  // stop and stay open.
  const handleError = useCallback(() => {
    const audio = audioRef.current;
    const failedUrl = currentSession?.audio_url;
    if (!audio || !currentSession || !failedUrl) return;
    if (refreshedUrlRef.current === failedUrl) {
      setIsPlaying(false);
      return;
    }
    const sessionId = currentSession.id;
    // A resume seek still waiting keeps its position (the element reports 0 until then).
    pendingSeekRef.current ??= audio.currentTime || currentTime;
    void fetchPlayableUrl(sessionId).then((result) => {
      if (result.kind === 'denied') {
        pendingSeekRef.current = null;
        closePlayer();
        return;
      }
      if (result.kind === 'failed' || result.url === failedUrl) {
        pendingSeekRef.current = null;
        setIsPlaying(false);
        return;
      }
      refreshedUrlRef.current = result.url;
      setAudioUrl(sessionId, result.url);
    });
  }, [currentSession, currentTime, fetchPlayableUrl, setAudioUrl, setIsPlaying, closePlayer]);

  // Handle time updates
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      recordPosition(false);
    }
  }, [setCurrentTime, recordPosition]);

  // Handle loaded metadata
  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setDuration(audio.duration);
    refreshedUrlRef.current = null; // this URL works; it may be refreshed when it expires
    if (pendingSeekRef.current !== null) {
      audio.currentTime = pendingSeekRef.current;
      setCurrentTime(pendingSeekRef.current);
      pendingSeekRef.current = null;
    }
  }, [setDuration, setCurrentTime]);

  // Handle audio end
  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    if (currentSession) forgetListening(currentSession.id);
  }, [setIsPlaying, currentSession, forgetListening]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    recordPosition(true);
  }, [setIsPlaying, recordPosition]);

  const handleClose = useCallback(() => {
    recordPosition(true);
    closePlayer();
  }, [recordPosition, closePlayer]);

  // Play/Pause toggle
  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, setIsPlaying]);

  // Skip forward/backward
  const skip = useCallback((seconds: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(
        0,
        Math.min(audioRef.current.currentTime + seconds, duration)
      );
    }
  }, [duration]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  }, [isMuted, setIsMuted]);

  // Handle volume change
  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      setIsMuted(newVolume === 0);
    }
  }, [setVolume, setIsMuted]);

  // Seek to a position based on X coordinate
  const seekToPosition = useCallback((clientX: number) => {
    if (!progressRef.current || !audioRef.current) return;

    const rect = progressRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const newTime = percent * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration, setCurrentTime]);

  // Handle progress bar click (desktop)
  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    seekToPosition(e.clientX);
  }, [seekToPosition]);

  // Handle touch drag on progress bar (mobile)
  const handleProgressTouch = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    const touch = e.touches[0];
    if (touch) {
      seekToPosition(touch.clientX);
    }
  }, [seekToPosition]);

  // Track the previous session to detect actual session changes
  const prevSessionRef = useRef<PlayableSession | null>(null);

  // Auto-play when session changes
  useEffect(() => {
    // No URL yet: wait for it (the fetch effect above), keep the play intent.
    if (!currentSession || !currentSession.audio_url || !audioRef.current) return;

    const audio = audioRef.current;
    const isNewSession = prevSessionRef.current?.id !== currentSession.id;
    prevSessionRef.current = currentSession;

    if (startAt !== null) {
      // "Nastavi slušanje": start at the stored position, now or once the metadata has loaded.
      clearStartAt();
      if (audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
        pendingSeekRef.current = null;
        audio.currentTime = startAt;
      } else {
        pendingSeekRef.current = startAt;
      }
      audio.play().catch(() => {
        setIsPlaying(false);
      });
    } else if (isNewSession) {
      pendingSeekRef.current = null;
      // New session: reset to beginning and play
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        setIsPlaying(false);
      });
    } else if (isPlaying && audioRef.current.paused) {
      // Same session resumed (e.g., returning from background/notification):
      // Keep current position, just resume playback
      audioRef.current.play().catch(() => {
        setIsPlaying(false);
      });
    }
  }, [currentSession, isPlaying, setIsPlaying, startAt, clearStartAt]);

  // Sync audio element state
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.muted = isMuted;
    }
  }, [volume, isMuted]);

  // Media Session API — enables lock screen controls and proper audio focus
  useEffect(() => {
    if (!('mediaSession' in navigator) || !currentSession) return;

    navigator.mediaSession.metadata = new MediaMetadata({
      title: currentSession.title,
      artist: 'Lukaaji',
      ...(currentSession.image_url ? {
        artwork: [{ src: currentSession.image_url, sizes: '512x512', type: 'image/jpeg' }],
      } : {}),
    });

    navigator.mediaSession.setActionHandler('play', () => {
      audioRef.current?.play();
      setIsPlaying(true);
    });
    navigator.mediaSession.setActionHandler('pause', () => {
      audioRef.current?.pause();
      setIsPlaying(false);
    });
    navigator.mediaSession.setActionHandler('seekbackward', () => skip(-10));
    navigator.mediaSession.setActionHandler('seekforward', () => skip(10));
    navigator.mediaSession.setActionHandler('seekto', (details) => {
      if (audioRef.current && details.seekTime != null) {
        audioRef.current.currentTime = details.seekTime;
        setCurrentTime(details.seekTime);
      }
    });

    return () => {
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
      navigator.mediaSession.setActionHandler('seekbackward', null);
      navigator.mediaSession.setActionHandler('seekforward', null);
      navigator.mediaSession.setActionHandler('seekto', null);
    };
  }, [currentSession, setIsPlaying, setCurrentTime, skip]);

  // Update Media Session position state for lock screen progress bar
  useEffect(() => {
    if (!('mediaSession' in navigator) || !duration) return;
    try {
      navigator.mediaSession.setPositionState({
        duration,
        playbackRate: 1,
        position: Math.min(currentTime, duration),
      });
    } catch {
      // setPositionState can throw if values are invalid
    }
  }, [currentTime, duration]);

  if (!isVisible || !currentSession) return null;

  // Session is already localized by the hooks, use title directly
  const title = currentSession.title;
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      data-testid="fixed-audio-player"
      className="fixed bottom-[calc(var(--bottom-nav-height)+env(safe-area-inset-bottom))] left-0 right-0 z-40 bg-card border-t shadow-lg"
    >
      {/* Hidden audio element - global player */}
      <audio
        ref={audioRef}
        data-global-player="true"
        src={currentSession.audio_url ?? undefined}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onError={handleError}
        onEnded={handleEnded}
        onPlay={() => setIsPlaying(true)}
        onPause={handlePause}
      />

      <div className="container py-2 sm:py-3">
        {/* Progress bar — touch-friendly */}
        <div
          ref={progressRef}
          className="relative h-1.5 bg-muted rounded-full cursor-pointer mb-2 sm:mb-3 touch-none"
          onClick={handleProgressClick}
          onTouchStart={handleProgressTouch}
          onTouchMove={handleProgressTouch}
        >
          <div
            className="absolute h-full bg-primary rounded-full"
            style={{ width: `${progressPercent}%` }}
          />
          {/* Touch handle */}
          <div
            className="absolute h-4 w-4 bg-primary rounded-full -top-[5px] shadow-sm"
            style={{ left: `calc(${progressPercent}% - 8px)` }}
          />
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          {/* Session info - clickable to open session details */}
          <button
            onClick={() => navigate(ROUTES.sessionDetail(currentSession.id))}
            className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
          >
            {currentSession.image_url ? (
              <img
                src={currentSession.image_url}
                alt={title}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover flex-shrink-0"
              />
            ) : (
              <SessionImageFallback
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex-shrink-0"
                iconClassName="h-4 w-4 sm:h-5 sm:w-5"
              />
            )}
            <div className="min-w-0">
              <h4 className="font-medium text-xs sm:text-sm truncate">{title}</h4>
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                {formatTime(currentTime)} / {formatTime(duration)}
              </p>
            </div>
          </button>

          {/* Controls — all targets >= 44px (mobile-first tap target rule) */}
          <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
            {/* Skip back — visible at all widths including 320px */}
            <button
              onClick={() => skip(-10)}
              className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-accent transition-colors"
              aria-label={t('skipBack')}
            >
              <SkipBack className="h-4 w-4" />
            </button>

            {/* Play/Pause */}
            <button
              onClick={togglePlay}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              aria-label={isPlaying ? t('pause') : t('play')}
            >
              {isPlaying ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="h-5 w-5 ml-0.5" />
              )}
            </button>

            {/* Skip forward — visible at all widths including 320px */}
            <button
              onClick={() => skip(10)}
              className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-accent transition-colors"
              aria-label={t('skipForward')}
            >
              <SkipForward className="h-4 w-4" />
            </button>

            {/* Volume (hidden on mobile) */}
            <div className="hidden sm:flex items-center gap-2 ml-2">
              <button
                onClick={toggleMute}
                className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-accent transition-colors"
                aria-label={isMuted ? t('unmute') : t('mute')}
              >
                {isMuted || volume === 0 ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="w-16 h-1 accent-primary"
                aria-label={t('volume')}
              />
            </div>

            {/* Close */}
            <button
              onClick={handleClose}
              className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-accent transition-colors"
              aria-label={t('closePlayer')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
